import React, { useState, useEffect } from 'react';
import {
  Modal,
  Typography,
  Tag,
  Image,
} from 'antd';
import {
  FileTextOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import collectionService from '../../services/collectionService';
import { DetailedCollection } from '../../types/collections';
import { useSnackbar } from 'notistack';

const { Text } = Typography;

interface CollectionDetailModalProps {
  visible: boolean;
  collectionId: string | null;
  onCancel: () => void;
}

const CollectionDetailModal: React.FC<CollectionDetailModalProps> = ({
  visible,
  collectionId,
  onCancel,
}) => {
  const [detailedCollection, setDetailedCollection] = useState<DetailedCollection | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchDetailedCollection = async (id: string) => {
    setDetailLoading(true);
    try {
      const response = await collectionService.getDetailedCollection(id);
      setDetailedCollection(response.data);
    } catch (error) {
      enqueueSnackbar('Lỗi khi tải chi tiết bộ sưu tập!', { variant: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (visible && collectionId) {
      fetchDetailedCollection(collectionId);
    } else {
      setDetailedCollection(null);
    }
  }, [visible, collectionId]);

  const handleCancel = () => {
    setDetailedCollection(null);
    onCancel();
  };

  console.log("thongnv : ", detailedCollection?.images?.[0])

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width="80vw"
      style={{ top: 20, maxWidth: '1200px' }}
      bodyStyle={{ padding: 0, maxHeight: '90vh', overflow: 'auto' }}
      closeIcon={<CloseOutlined style={{ fontSize: '20px', color: '#000' }} />}
    >
      {detailLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              border: '4px solid #f3f3f3', 
              borderTop: '4px solid #1890ff', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <Text style={{ fontSize: '18px' }}>Đang tải chi tiết...</Text>
          </div>
        </div>
      ) : detailedCollection ? (
        <div>
          {/* Hero Section */}
          <section style={{ 
            position: 'relative', 
            width: '100%', 
            height: '400px', 
            backgroundColor: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
            <img 
              src={detailedCollection.images?.[0]} 
              alt="Hero" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover', 
                opacity: 1
              }} 
            />
            <div style={{ 
              position: 'relative',
              zIndex: 2,
              textAlign: 'center',
              color: 'white',
              width: '100%',
              padding: '0 16px'
            }}>
              <h2 style={{ 
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontWeight: 700,
                fontSize: '20.69px',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                marginBottom: '12px'
              }}>COLLECTION</h2>
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <h1 style={{ 
                  fontFamily: "'SVN-Beatrice', sans-serif",
                  fontWeight: 500,
                  fontSize: '56.34px',
                  textTransform: 'uppercase',
                  marginBottom: '12px',
                  lineHeight: 1.2
                }}>
                  {detailedCollection.name}
                </h1>
                <p style={{ 
                  fontFamily: "'Be Vietnam Pro', sans-serif",
                  fontWeight: 500,
                  fontSize: '12px',
                  opacity: 0.75,
                  maxWidth: '800px',
                  margin: '0 auto',
                  lineHeight: 1.6
                }}>
                  {detailedCollection.description?.replace(/<[^>]*>/g, '').substring(0, 300)}...
                </p>
              </div>
            </div>
          </section>

          {/* Sections */}
          {detailedCollection.sections && detailedCollection.sections.length > 0 && (
            <section style={{ backgroundColor: '#d9d9d9', padding: '80px 0' }}>
              <div style={{ width: '60%', margin: '0 auto' }}>
                {detailedCollection.sections.map((section, idx) => (
                  <div key={section._id} style={{ paddingBottom: idx < detailedCollection.sections.length - 1 ? '80px' : 0 }}>
                    {/* Title + Description */}
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                      <h3 style={{ 
                        fontFamily: "'Be Vietnam Pro', sans-serif",
                        fontWeight: 400,
                        fontSize: '24px',
                        marginBottom: '16px',
                        color: '#000'
                      }}>
                        {section.name}
                      </h3>
                      <p style={{ 
                        fontFamily: "'Be Vietnam Pro', sans-serif",
                        fontWeight: 500,
                        fontSize: '12px',
                        color: '#6c757d',
                        maxWidth: '800px',
                        margin: '0 auto',
                        lineHeight: 1.6
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: section.description?.substring(0, 300) + '...' || 'Không có mô tả' 
                      }}
                      />
                    </div>

                    {/* Images Grid */}
                    {section.images && section.images.length > 0 && (
                      <div style={{ marginBottom: '40px' }}>
                        {/* Render ảnh responsive */}
                        {section.images.length === 1 && (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '16px', 
                            width: '100%', 
                            alignItems: 'center' 
                          }}>
                            <div style={{ 
                              width: '100%', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center' 
                            }}>
                              <img 
                                src={section.images[0]} 
                                alt="Section 1" 
                                style={{ 
                                  width: '100%', 
                                  height: 'auto', 
                                  borderRadius: '8px', 
                                  objectFit: 'cover', 
                                  maxWidth: '800px' 
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {section.images.length === 2 && (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            gap: '16px', 
                            width: '100%', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                          }}>
                            {section.images.map((img, i) => (
                              <div key={i} style={{ 
                                flex: 1, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                              }}>
                                <img 
                                  src={img} 
                                  alt={`Section ${i + 1}`} 
                                  style={{ 
                                    width: '100%', 
                                    height: 'auto', 
                                    borderRadius: '8px', 
                                    objectFit: 'cover', 
                                    maxWidth: '500px' 
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {section.images.length === 3 && (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            width: '100%', 
                            gap: '8px', 
                            alignItems: 'stretch',
                            minHeight: '400px'
                          }}>
                            {/* Ảnh lớn bên trái */}
                            <div style={{ 
                              flex: 1, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center' 
                            }}>
                              <img 
                                src={section.images[0]} 
                                alt="Section 1" 
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  borderRadius: '8px', 
                                  objectFit: 'cover',
                                  aspectRatio: '2/3',
                                  minHeight: '400px'
                                }}
                              />
                            </div>
                            {/* Hai ảnh nhỏ xếp dọc bên phải */}
                            <div style={{ 
                              flex: 1, 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: '8px', 
                              height: '100%', 
                              justifyContent: 'space-between' 
                            }}>
                              <div style={{ 
                                flex: 1, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                              }}>
                                <img 
                                  src={section.images[1]} 
                                  alt="Section 2" 
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    borderRadius: '8px', 
                                    objectFit: 'cover',
                                    minHeight: '196px'
                                  }}
                                />
                              </div>
                              <div style={{ 
                                flex: 1, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                              }}>
                                <img 
                                  src={section.images[2]} 
                                  alt="Section 3" 
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    borderRadius: '8px', 
                                    objectFit: 'cover',
                                    minHeight: '196px'
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {section.images.length === 4 && (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '16px',
                            width: '100%',
                            alignItems: 'center'
                          }}>
                            {section.images.map((img, i) => (
                              <div key={i} style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <img
                                  src={img}
                                  alt={`Section ${i + 1}`}
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: '8px',
                                    objectFit: 'cover',
                                    maxWidth: '500px'
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {section.images.length > 4 && (
                          <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '24px'
                          }}>
                            {section.images.slice(0, 4).map((image, imgIdx) => (
                              <div key={imgIdx}>
                                <img
                                  src={image}
                                  alt={`${section.name} ${imgIdx + 1}`}
                                  style={{ 
                                    width: '100%',
                                    height: '400px',
                                    objectFit: 'cover'
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Section Products */}
                    {section.products && section.products.length > 0 && (
                      <div style={{ marginTop: '40px' }}>
                        <h4 style={{ 
                          fontSize: '20px',
                          fontWeight: 600,
                          textAlign: 'center',
                          marginBottom: '24px'
                        }}>Sản phẩm trong section</h4>
                        <div style={{ 
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '16px'
                        }}>
                          {section.products.slice(0, 4).map((product) => (
                            <div key={product._id} style={{ 
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '1px solid #e9ecef'
                            }}>
                              <div style={{ position: 'relative' }}>
                                <Image
                                  src={product.images?.[0]}
                                  alt={product.name}
                                  style={{ 
                                    width: '100%',
                                    height: '200px',
                                    objectFit: 'cover'
                                  }}
                                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                                />
                                <Tag 
                                  color={product.status === 'available' ? 'success' : 'error'} 
                                  style={{ 
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px'
                                  }}
                                >
                                  {product.status === 'available' ? 'Có sẵn' : 'Hết hàng'}
                                </Tag>
                              </div>
                              <div style={{ padding: '12px' }}>
                                <h6 style={{ 
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  marginBottom: '8px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>{product.name}</h6>
                                <p style={{ 
                                  color: '#dc3545',
                                  fontWeight: 'bold',
                                  margin: 0
                                }}>
                                  {product.price?.regular?.toLocaleString('vi-VN') || '0'}đ
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Statistics */}
          <section style={{ padding: '40px 0', backgroundColor: '#000', color: 'white' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '24px',
                textAlign: 'center'
              }}>
                <div>
                  <div style={{ 
                    fontSize: '48px',
                    fontWeight: 'bold',
                    color: '#007bff',
                    marginBottom: '8px'
                  }}>
                    {detailedCollection.products?.length || 0}
                  </div>
                  <div style={{ color: '#adb5bd' }}>Sản phẩm</div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: '48px',
                    fontWeight: 'bold',
                    color: '#28a745',
                    marginBottom: '8px'
                  }}>
                    {detailedCollection.sections?.length || 0}
                  </div>
                  <div style={{ color: '#adb5bd' }}>Sections</div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: '48px',
                    fontWeight: 'bold',
                    color: '#ffc107',
                    marginBottom: '8px'
                  }}>
                    {detailedCollection.news?.length || 0}
                  </div>
                  <div style={{ color: '#adb5bd' }}>Tin tức</div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: '48px',
                    fontWeight: 'bold',
                    color: '#17a2b8',
                    marginBottom: '8px'
                  }}>
                    {detailedCollection.images?.length || 0}
                  </div>
                  <div style={{ color: '#adb5bd' }}>Hình ảnh</div>
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#6c757d' }}>
          <div style={{ fontSize: '96px', marginBottom: '16px' }}>📄</div>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>Không thể tải thông tin chi tiết</div>
          <div style={{ fontSize: '14px' }}>Vui lòng thử lại sau</div>
        </div>
      )}
    </Modal>
  );
};

export default CollectionDetailModal; 